// Opening a product's sign-in, the one way for the connect form, Sign in
// again and the /connect ticket page. The address comes from the product's
// own server, so it is opened only when it is an absolute https URL (never
// `javascript:`, `data:` or plain http; the server refuses those too), and
// the vendor's tab never keeps a handle on this one: the blank tab opened in
// the click (a popup blocker lets that one through) has its opener cut
// before it leaves for the vendor, and a tab opened later has none at all.
// Completion is learned by reading the list again, never from the tab.
// Tested in connectionsModel.selftest.ts.

/** An address Corvus may send someone to sign in: an absolute https URL. */
export function isHttpsUrl(url: string | null | undefined): url is string {
  if (!url) return false;
  try {
    return new URL(url).protocol === "https:";
  } catch {
    return false;
  }
}

/** The blank tab opened in the click itself, for the consent once Corvus answers; null when it was blocked. */
export function preopenSignInTab(open: Window["open"] = window.open.bind(window)): Window | null {
  return open("", "_blank");
}

/** What became of the sign-in: shown in a tab, to be offered as a link (no tab could be seen to open), or refused. */
export type SignInOpened = "opened" | "link" | "refused";

type Tab = Pick<Window, "close"> & { opener: unknown; location: { href: string } };

/**
 * Send the sign-in to `url`: in `tab` (the one opened in the click) with
 * its opener cut first, else in a new tab opened without one. A refused
 * address closes the tab and opens nothing.
 */
export function openSignIn(url: string | null | undefined, tab: Tab | null, open: Window["open"] = window.open.bind(window)): SignInOpened {
  if (!isHttpsUrl(url)) {
    tab?.close();
    return "refused";
  }
  if (tab) {
    tab.opener = null;
    tab.location.href = url;
    return "opened";
  }
  // With noopener the browser answers null whether or not it opened, so the page offers the link as well.
  open(url, "_blank", "noopener,noreferrer");
  return "link";
}
